import { Box, ToggleButton, Checkbox, FormControlLabel } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Tooltip } from 'react-leaflet'
import DragHandle from '@mui/icons-material/DragHandle'

export const StatusRoundIcon = styled('div', {
  shouldForwardProp: (propName) => propName !== 'oval' && propName !== 'color',
})<{
  color: string
  oval?: boolean
}>(({ theme, color, oval }) => ({
  backgroundColor: color,
  width: oval ? theme.spacing(5) : theme.spacing(2),
  height: theme.spacing(2),
  borderRadius: oval ? theme.spacing(1) : '100%',
  marginRight: theme.spacing(2),
  fontSize: '10px',
  letterSpacing: '-0.33px',
  textAlign: 'center',
  color: 'white',
  lineHeight: 1.6,
  fontFamily: 'Arial',
  fontWeight: 'bold',
}))

export const OrderItem = styled(Box, {
  shouldForwardProp: (propName) =>
    propName !== 'smallPadding' &&
    propName !== 'hideBorders' &&
    propName !== 'defaultCursor',
})<{
  smallPadding?: boolean
  hideBorders?: boolean
  defaultCursor?: boolean
}>(({ theme, smallPadding, hideBorders, defaultCursor }) => ({
  padding: smallPadding
    ? `${theme.spacing(1)} ${theme.spacing(2)}`
    : `${theme.spacing(1)} ${theme.spacing(4)}`,
  cursor: defaultCursor ? 'default' : 'pointer',
  height: theme.spacing(9),
  borderBottom: hideBorders ? 0 : `1px solid ${theme.palette.neutral.grey7}`,
  '&:hover': {
    backgroundColor: hideBorders
      ? theme.palette.common.white
      : theme.palette.action.hover,
    '.MuiSvgIcon-root': {
      visibility: 'visible',
    },
  },
}))

export const OrderListingWrapper = styled('aside')(({ theme }) => ({
  width: '410px',
  overflowY: 'scroll',
  height: '100vh',
  padding: `${theme.spacing(2)} 0`,
}))

export const MapTooltip = styled(Tooltip)(({ theme }) => ({
  width: '80px',
  height: '30px',
  padding: 0,
  border: 'none',
  boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
  '&:before': {
    filter: 'drop-shadow(-5px 6px 9px #000000)',
    borderTopColor: theme.palette.commonColors.white,
    marginBottom: '-9px',
    zIndex: 1001,
  },
}))

export const RoundMapTooltip = styled(Tooltip)<{ color: string }>(
  ({ color }) => ({
    backgroundColor: color,
    border: 0,
    borderRadius: '16px',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:before': {
      borderTopColor: color,
      marginBottom: '-10px',
      marginLeft: '-5px',
    },
  })
)

export const HoverAndSelectedTriangle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: 'transparent',
  borderRight: '8px solid transparent',
  borderLeft: '8px solid transparent',
  borderTop: `8px solid ${theme.palette.commonColors.black}`,
  zIndex: 1000,
  width: 0,
  height: 0,
  bottom: '-7px',
}))

export const HoverTriangle = styled(Box, {
  shouldForwardProp: (propName) => propName !== 'firstSelectedOrder',
})<{
  firstSelectedOrder: boolean
}>(({ firstSelectedOrder, theme }) => ({
  position: 'absolute',
  background: 'transparent',
  borderRight: `${firstSelectedOrder ? '8px' : '6px'} solid transparent`,
  borderLeft: `${firstSelectedOrder ? '8px' : '6px'} solid transparent`,
  borderTop: firstSelectedOrder
    ? `9px solid ${theme.palette.primary.main}`
    : `6px solid ${theme.palette.commonColors.black}`,
  zIndex: firstSelectedOrder ? 2002 : 1000,
  width: 0,
  height: 0,
  bottom: '-6px',
}))

export const StyledTooltipBox = styled(Box, {
  shouldForwardProp: (propName) =>
    propName !== 'selected' && propName !== 'hovered',
})<{
  selected: boolean
  hovered: boolean
}>(({ selected, hovered, theme }) => ({
  width: '80px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  borderRadius: '3px',
  border: `1px solid ${
    hovered ? theme.palette.commonColors.black : 'transparent'
  }`,
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.commonColors.white,
  color: selected
    ? theme.palette.commonColors.white
    : theme.palette.commonColors.black,
  position: 'relative',
}))

export const ToggleRoutesButton = styled(ToggleButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 'none',
  color: theme.palette.text.primary,
  width: '100%',
  justifyContent: 'flex-start',
  borderRadius: theme.spacing(1),
  '&.MuiToggleButton-root': {
    textTransform: 'none',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  '&.Mui-disabled': {
    border: 'none',
    borderTopRightRadius: theme.spacing(1),
    borderTopleftRadius: theme.spacing(1),
    borderBottomRightRadius: theme.spacing(1),
    borderBottomLeftRadius: theme.spacing(1),
  },
}))

export const MapMarkerTitle = styled('div')<{
  color: string
  hovered: boolean
}>(({ color, hovered }) => ({
  width: '28px',
  height: '28px',
  display: 'flex',
  pointerEvents: 'auto',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  color: hovered ? 'white' : color,
  border: `2px solid ${color}`,
  cursor: hovered ? 'pointer' : 'default',
  backgroundColor: hovered ? color : 'white',
  textAlign: 'center',
}))

export const MapTooltipWrapper = styled('div')({
  position: 'relative',
  width: '80px',
  height: '30px',
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
})

export const DragHandleIcon = styled(DragHandle)(({ theme }) => ({
  fontSize: '14px',
  marginRight: theme.spacing(1),
  visibility: 'hidden',
}))

export const OrderCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  fill: theme.palette.icon.main,
  '&.Mui-checked svg': {
    fill: theme.palette.primary.main,
  },
  '&.Mui-disabled svg': {
    fill: theme.palette.action.disabled,
  },
}))

export const DottedLine = styled('div', {
  shouldForwardProp: (propName) =>
    propName !== 'driverPositionExists' && propName !== 'orderColor',
})<{
  length: number
  driverPositionExists: string
  orderColor: string
}>(({ length, driverPositionExists, orderColor }) => ({
  height: `calc(56px * ${length} + ${driverPositionExists})`,
  borderLeft: `2px dotted ${orderColor}`,
  position: 'absolute',
  width: '2px',
  top: '45px',
  left: '85px',
}))

export const FormLabel = styled(FormControlLabel)({
  width: '100%',
  '& > .MuiTypography-root': {
    width: '100%',
  },
  '& ~ .MuiTypography-root': {
    width: '100%',
  },
})

export const WarningTimeBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.commonColors.white,
  borderRadius: '3px',
  display: 'flex',
  justifyContent: 'center',
  width: theme.spacing(5),
}))
